<template>
  <admin-wrapper
    :straight-title="additionalModuleName"
    :inputs="inputs"
    :moduleEnabler="moduleEnabler"
    :is-form-fetched="isFormFetched"
    :has-buttons="failuresCutoffEnabled || failuresDispatcherEnabled"
    @save="saveCard"
  >
    <template #visualization.input>
      <predefined-styles class="mb-16" :layer-id="layerId" :data-source-name="dataSourceName"></predefined-styles>
    </template>
    <template #featureCard.input>
      <draggable-lists-manage-card
        :layer-id="layerId"
        @update:hasAnyRequiredOnRightSide="hasAnyRequiredOnRightSide = $event"
        :has-edit-attributes="true"
      >
      </draggable-lists-manage-card>
    </template>
  </admin-wrapper>
</template>

<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import DraggableListsManageCard from '@/components/DraggableListsManageCard';
import PredefinedStyles from '@/components/PredefinedStyles';

export default {
  name: 'FailuresSettings',
  components: {
    AdminWrapper,
    DraggableListsManageCard,
    PredefinedStyles,
  },
  data: () => ({
    isFormFetched: false,
    hasAnyRequiredOnRightSide: false,
  }),
  computed: {
    additionalModuleName: get('admin/additionalModules@FAILURES.verbose_name'),
    dataSourceName: get('admin/modulesMapping@failures.datasource_name'),
    isModuleEnabled: get('admin/settingsValues@failures_module_enabled'),
    layerId: get('admin/modulesMapping@failures.layer_id'),
    currentLayerSchema: get('layers/currentLayerSchema'),
    failuresCutoffEnabled: get('admin/settingsValues@failures_cutoff_enabled'),
    failuresDispatcherEnabled: get('admin/settingsValues@failures_dispatcher_enabled'),
    moduleEnabler() {
      return {
        name: 'failures_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
    inputs() {
      return [
        ...(this.failuresCutoffEnabled
          ? [
              {
                name: 'cutoff_valves_label',
                disabled: !this.isModuleEnabled,
                dataType: {
                  name: 'text',
                },
                align: 'start',
                translationPath: 'admin.locksLabel',
                subtitle: `admin.locksLabelSubtitle`,
              },
              {
                name: 'cutoff_status_filter',
                dataType: {
                  name: 'select',
                },
                items: this.waterPipesDataSourceStatusAttributeValues,
                multiple: true,
                autoConvertSelect: false,
                align: 'start',
                translationPath: 'admin.cutOffStatus',
                subtitle: `admin.cutOffStatusSubtitle`,
              },
              {
                name: 'cutoff_net_kind_filter',
                dataType: {
                  name: 'select',
                },
                items: this.waterPipesDataSourceNetKindAttributeValues,
                multiple: true,
                autoConvertSelect: false,
                align: 'start',
                translationPath: 'admin.cutOffNetKind',
                subtitle: `admin.cutOffNetKindSubtitle`,
              },
            ]
          : []),
        ...(this.failuresDispatcherEnabled
          ? [
              {
                name: 'visualization',
                subtitle: `admin.failuresVisualizationSubtitle`,
                translationPath: 'admin.visualizationOnMap',
                isCustom: true,
                align: 'start',
              },
              {
                name: 'featureCard',
                clearable: true,
                translationPath: 'admin.featureCard',
                isCustom: true,
                subtitle: this.$i18n.t('admin.setFeatureCard', { type: this.$i18n.t('admin.objectType') }),
                align: 'start',
              },
            ]
          : []),
      ];
    },

    dataSources: get('layers/metadata'),
    layers: get('layers/layers'),
    waterPipesLayerId: get('admin/modulesMapping@failures.cutoff_analysis_layers_ids.water_pipes'),
    waterPipesDataSourceName() {
      return this.layers[this.waterPipesLayerId]?.data_source_name || '';
    },
    waterPipesDataSourceAttributes() {
      return this.dataSources[this.waterPipesDataSourceName]?.attributes_schema?.attributes || [];
    },
    waterPipesDataSourceStatusAttributeValues() {
      return this.waterPipesDataSourceAttributes.find(attribute => attribute.name === 'status')?.allowed_values || [];
    },
    waterPipesDataSourceNetKindAttributeValues() {
      return this.waterPipesDataSourceAttributes.find(attribute => attribute.name === 'net_kind')?.allowed_values || [];
    },
  },
  methods: {
    getModulesMapping: call('admin/getModulesMapping'),
    getDataSourceForm: call('layers/getDataSourceForm'),
    getLayers: call('layers/getLayers'),
    getLayerMetadata: call('layers/getLayerMetadata'),
    getFeaturesLayer: call('layers/getFeaturesLayer'),
    editLayer: call('layers/editLayer'),
    async saveCard() {
      if (this.failuresDispatcherEnabled) {
        await this.editLayer({
          layer_type: 'features_layers',
          layer_id: this.layerId,
          body: {
            form_schema: {
              elements: this.currentLayerSchema,
            },
          },
        });
      }
    },
    async init() {
      await this.getModulesMapping();
      if (this.failuresDispatcherEnabled) {
        await this.getLayers();
        await Promise.all([
          this.getDataSourceForm(this.dataSourceName),
          this.getFeaturesLayer(this.layerId),
          this.getLayerMetadata({ dataSource: this.dataSourceName }),
          this.getLayerMetadata({ dataSource: this.waterPipesDataSourceName }),
        ]);
      }
      this.isFormFetched = true;
    },
  },
  watch: {
    isModuleEnabled(nV) {
      if (nV) {
        this.isFormFetched = false;
        this.init();
      }
    },
  },
  created() {
    if (this.isModuleEnabled) {
      this.init();
    } else {
      this.isFormFetched = true;
    }
  },
};
</script>
